import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api.js'
import { Card, Alert, StatusPill, Progress, Modal } from '../../lib.jsx'

// Etykiety PL dla typów zadań `pdfgen_*` — lista zweryfikowana wobec `@celery.task(name="jobs.pdfgen_...")`
// w `app/pdfgen_worker.py` (9 zadań, kompletna lista na dziś). Nowy typ zadania i tak wymaga zmiany
// kodu workera, więc dopisanie etykiety tutaj przy tej okazji jest naturalne — stąd brak osobnego
// endpointu do wyliczania tej listy z bazy (patrz `app/admin_jobs.py` docstring).
const TYPE_PL = {
  pdfgen_render: 'Render testowy',
  pdfgen_import: 'Import',
  pdfgen_compose: 'Kompozycja',
  pdfgen_change_partial: 'Zmiana layoutu',
  pdfgen_fill_images: 'Generowanie obrazów',
  pdfgen_export: 'Eksport',
  pdfgen_design_template: 'Projektowanie szablonu (AI)',
  pdfgen_review_template: 'AI Reviewer',
  pdfgen_ai_fill_prompts: 'AI: uzupełnianie promptów',
}
const ALL_TYPES = Object.keys(TYPE_PL)

const QUICK_FILTERS = [
  { key: 'all', label: 'Wszystkie', status: null },
  { key: 'active', label: 'W toku', status: 'queued,running' },
  { key: 'error', label: 'Błędy', status: 'error' },
  { key: 'done', label: 'Ukończone', status: 'done' },
]

const NO_PROJECT_KEY = '__none__'
const PAGE_SIZE = 20
const POLL_MS = 5000

// Ten sam fix na parsowanie dat serwera (naiwny UTC bez "Z") co `lib.jsx`'s `useJobElapsed` — bez
// tego przeglądarka czyta timestamp jako czas LOKALNY (patrz komentarz w lib.jsx), co dawałoby
// przesunięte "utworzono"/czas trwania w tym panelu tak samo jak dawniej w pasku postępu.
function parseServerUtc(s) {
  if (!s) return NaN
  const iso = /[Zz]|[+-]\d\d:\d\d$/.test(s) ? s : s + 'Z'
  return new Date(iso).getTime()
}

function formatDateTime(s) {
  const ms = parseServerUtc(s)
  if (Number.isNaN(ms)) return '—'
  return new Date(ms).toLocaleString('pl-PL')
}

function formatDuration(start, end) {
  const startMs = parseServerUtc(start)
  if (Number.isNaN(startMs)) return '—'
  const endMs = end ? parseServerUtc(end) : Date.now()
  const sec = Math.max(0, Math.floor((endMs - startMs) / 1000))
  const mm = String(Math.floor(sec / 60)).padStart(2, '0')
  const ss = String(sec % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

const LEVEL_CLASS = { info: '', warn: 'pill-canceled', error: 'pill-error' }

function JobLogTimeline({ log }) {
  if (!log || log.length === 0) return <div className="empty">Brak wpisów w logu.</div>
  return (
    <div className="unit-row" style={{ maxHeight: 360, overflowY: 'auto' }}>
      {log.map((entry, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
          <span className="mono small muted" style={{ whiteSpace: 'nowrap' }}>{entry.t}</span>
          {entry.pct != null && <span className="pill small">{entry.pct}%</span>}
          <span className={`small ${entry.level === 'error' ? 'pill pill-error' : entry.level === 'warn' ? 'pill pill-canceled' : ''}`}
            style={{ flex: 1 }}>{entry.msg}</span>
        </div>
      ))}
    </div>
  )
}

function JobDetailModal({ jobId, onClose }) {
  const [detail, setDetail] = useState(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    let cancelled = false
    api.adminJobDetail(jobId).then(d => { if (!cancelled) setDetail(d) }).catch(e => { if (!cancelled) setErr(e.message) })
    return () => { cancelled = true }
  }, [jobId])

  return (
    <Modal title="Szczegóły zadania" onClose={onClose}>
      <Alert kind="err">{err}</Alert>
      {!detail ? <div className="empty">Wczytywanie…</div> : (
        <>
          <div className="row" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
            <div><b>Typ:</b> {TYPE_PL[detail.type] || detail.type}</div>
            <div><b>Status:</b> <StatusPill status={detail.status} /></div>
            <div><b>Użytkownik:</b> {detail.username || detail.user_id}</div>
            <div><b>Projekt:</b> {detail.project_name || 'Bez projektu (zadanie szablonu)'}</div>
          </div>
          {detail.status !== 'done' && detail.status !== 'error' && <Progress value={detail.progress} />}
          {detail.stage && <div className="muted small" style={{ margin: '6px 0' }}>{detail.stage}</div>}
          {detail.error_msg && <Alert kind="err">{detail.error_msg}</Alert>}

          <div className="divider" />
          <b className="small">Parametry zadania</b>
          <pre className="mono small" style={{ maxHeight: 200, overflow: 'auto', background: 'var(--surface-2)', padding: 10, borderRadius: 8 }}>
            {JSON.stringify(detail.params, null, 2)}
          </pre>

          <div className="divider" />
          <b className="small">Log zdarzeń</b>
          <JobLogTimeline log={detail.log} />
        </>
      )}
    </Modal>
  )
}

function JobCells({ job, onOpen }) {
  const active = job.status === 'queued' || job.status === 'running'
  return <>
      <td className="small">{TYPE_PL[job.type] || job.type}</td>
      <td><StatusPill status={job.status} /></td>
      <td style={{ minWidth: 120 }}>
        {active ? <Progress value={job.progress} /> : <span className="muted small">{job.progress}%</span>}
      </td>
      <td className="small muted" title={job.stage || ''} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {job.stage || '—'}
      </td>
      <td className="small">{job.username || job.user_id}</td>
      <td className="small muted">{formatDateTime(job.created_at)}</td>
      <td className="small muted mono">{formatDuration(job.started_at || job.created_at, job.finished_at)}</td>
      <td><button type="button" className="btn btn-sm btn-ghost" onClick={() => onOpen(job.id)}>Szczegóły</button></td>
    </>
}

function JobRow({ job, onOpen }) { return <tr><JobCells job={job} onOpen={onOpen} /></tr> }

// Grupuje płaską, już posortowaną (created_at DESC) listę wg project_id. Kolejność grup = kolejność
// pierwszego napotkania joba (czyli najświeższego w danej grupie, bo lista wejściowa jest DESC) —
// dokładnie "grupa z najnowszą aktywnością pierwsza", bez dodatkowego sortowania grup.
function groupByProject(items) {
  const order = []
  const groups = new Map()
  for (const job of items) {
    const key = job.project_id || NO_PROJECT_KEY
    if (!groups.has(key)) { groups.set(key, { key, project_id: job.project_id, project_name: job.project_name, jobs: [] }); order.push(key) }
    groups.get(key).jobs.push(job)
  }
  return order.map(k => groups.get(k))
}

export default function Jobs() {
  const navigate = useNavigate()
  const [quick, setQuick] = useState('all')
  const [type, setType] = useState('')
  const [qInput, setQInput] = useState('')
  const [q, setQ] = useState('')
  const [offset, setOffset] = useState(0)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [openJobId, setOpenJobId] = useState(null)
  const [expanded, setExpanded] = useState(new Set())
  const [sort, setSort] = useState('newest')
  const qTimer = useRef()
  const pollTimer = useRef()

  // Debounce wyszukiwania (300ms) — ta sama zwłoka co `UserSearch` w lib.jsx.
  useEffect(() => {
    clearTimeout(qTimer.current)
    qTimer.current = setTimeout(() => { setQ(qInput.trim()); setOffset(0) }, 300)
    return () => clearTimeout(qTimer.current)
  }, [qInput])

  const quickStatus = QUICK_FILTERS.find(f => f.key === quick)?.status || null

  const load = async () => {
    setBusy(true); setErr('')
    try {
      const params = { limit: '500', offset: '0' }
      if (quickStatus) params.status = quickStatus
      if (type) params.type = type
      if (q) params.q = q
      const res = await api.adminJobsList(params)
      setItems(res.items); setTotal(res.total)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  useEffect(() => { load() }, [quick, type, q, offset])

  // Odświeżanie na żywo co 5s, TYLKO gdy bieżący (przefiltrowany) wynik zawiera zadanie
  // queued/running — inaczej pollowanie zatrzymane (brak sensu obciążać backend na pusto).
  useEffect(() => {
    clearInterval(pollTimer.current)
    const hasActive = items.some(j => j.status === 'queued' || j.status === 'running')
    if (!hasActive) return undefined
    pollTimer.current = setInterval(load, POLL_MS)
    return () => clearInterval(pollTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, quick, type, q, offset])

  const groups = useMemo(() => {
    const values = groupByProject(items)
    if (sort === 'name') values.sort((a, b) => (a.project_name || '').localeCompare(b.project_name || '', 'pl'))
    if (sort === 'oldest') values.reverse()
    return values
  }, [items, sort])
  const page = Math.floor(offset / PAGE_SIZE) + 1
  const pages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE))
  const visibleGroups = groups.slice(offset, offset + PAGE_SIZE)

  return (
    <>
      <div className="topbar">
        <div><h1>Zadania ebooków</h1><div className="sub">Podgląd zadań w tle (żywe i archiwalne) — import, skład, eksport, AI Designer/Reviewer.</div></div>
      </div>
      <div className="content">
        <Card>
          <div className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-end', gap: 10 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {QUICK_FILTERS.map(f => (
                <button key={f.key} type="button" className={`btn btn-sm ${quick === f.key ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => { setQuick(f.key); setOffset(0) }}>{f.label}</button>
              ))}
            </div>
            <select value={type} onChange={e => { setType(e.target.value); setOffset(0) }} style={{ maxWidth: 240 }}>
              <option value="">Wszystkie typy</option>
              {ALL_TYPES.map(t => <option key={t} value={t}>{TYPE_PL[t]}</option>)}
            </select>
            <input value={qInput} onChange={e => setQInput(e.target.value)} placeholder="Szukaj po projekcie lub użytkowniku…" style={{ maxWidth: 280 }} />
            <select value={sort} onChange={e => { setSort(e.target.value); setOffset(0) }}><option value="newest">Najnowsza aktywność</option><option value="oldest">Najstarsza aktywność</option><option value="name">Nazwa projektu A–Z</option></select>
          </div>
        </Card>

        <Alert kind="err">{err}</Alert>

        {!busy && items.length === 0 ? <div className="empty">Brak zadań spełniających kryteria.</div> : (
          <Card><table><thead><tr><th>Projekt</th><th>Typ</th><th>Status</th><th>Postęp</th><th>Etap</th><th>Użytkownik</th><th>Utworzono</th><th>Czas</th><th /></tr></thead><tbody>{visibleGroups.flatMap(g => {
            const isOpen = expanded.has(g.key); const shown = isOpen ? g.jobs : g.jobs.slice(0, 1)
            return shown.map((job, index) => <React.Fragment key={job.id}><tr className={index ? 'history-row' : ''}><td>{index === 0 ? <><b>{g.project_name || 'Bez projektu'}</b><div><button className="btn btn-sm btn-ghost" onClick={() => setExpanded(current => { const next = new Set(current); next.has(g.key) ? next.delete(g.key) : next.add(g.key); return next })}>{isOpen ? 'Ukryj historię' : `Pokaż historię (${Math.max(0, g.jobs.length - 1)})`}</button>{g.project_id && <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/projects/${g.project_id}`)}>Otwórz →</button>}</div></> : <span className="muted">↳ historia</span>}</td><JobCells job={job} onOpen={setOpenJobId}/></tr></React.Fragment>)
          })}</tbody></table></Card>
        )}

        {pages > 1 && (
          <div className="pager">
            <button type="button" className="btn btn-sm btn-ghost" disabled={page <= 1} onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}>‹ Poprzednia</button>
            <span className="muted small">Strona {page} z {pages} · {groups.length} projektów / {total} zadań</span>
            <button type="button" className="btn btn-sm btn-ghost" disabled={page >= pages} onClick={() => setOffset(o => o + PAGE_SIZE)}>Następna ›</button>
          </div>
        )}
      </div>
      {openJobId && <JobDetailModal jobId={openJobId} onClose={() => setOpenJobId(null)} />}
    </>
  )
}
